import React, { useEffect, useMemo } from 'react';
import { Link, useLocation, useParams } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faLock,
  faEye,
  faArrowLeft,
  faLink,
  faCloudArrowUp,
  faTriangleExclamation,
  faRotateLeft,
} from '@fortawesome/free-solid-svg-icons';
import CgRadixButton from 'modules/Common/ui/CgRadixButton';
import DiagramFooterPanel from 'modules/Diagrams/DiagramEditor/DiagramFooterPanel';
import { useFetchInstallationDiagrams } from 'modules/OperatorInstallations/hooks/useFetchInstallationDiagrams';
import { useSupplierDiagram } from 'modules/FreeSupplier/useSupplierDiagram';
import { SupplierRoutePath, buildSupplierDraftReviewPath } from 'modules/FreeSupplier/routes';
import { useMockData } from '../state/MockDataContext';
import { useUpgradeModal } from '../components/UpgradeModal';
import { SupplierPanelLoader } from '../components/SupplierLoadingStates';
import { lazyWithRetry } from 'modules/Common/utils/lazyWithRetry';
import { trackPreload } from 'modules/Common/utils/chunkLoadState';

// @xyflow/react (the `flow` chunk, 298 kB raw / 99 kB gzip) lives only under
// Diagrams/, but this page is reachable from an EAGER surface, so a static
// import of the canvas pinned the chunk into the entry graph for every visitor
// regardless of the lazy Diagrams route. Loaded on demand instead.
//
// The Suspense boundary is ContentWrapper's (_app.jsx), which every route
// surface renders inside, so no boundary is needed here.
//
// The page starts this import on mount, so the canvas downloads while the
// diagram data loads instead of after it.
const loadCanvas = () => import('modules/Diagrams/components/ReadOnlyDiagramCanvas');
const ReadOnlyDiagramCanvas = lazyWithRetry(loadCanvas);

// Read-only render of the operator's REAL production diagram, through the shared
// ReadOnlyDiagramCanvas — so the free-tier view matches exactly how diagrams look everywhere
// else in the product, and cannot drift away from them (no illustrative mockup).

export default function DiagramViewPage() {
  useEffect(() => {
    trackPreload(loadCanvas()).catch(() => {});
  }, []);
  const { data } = useMockData();
  const { openUpgrade } = useUpgradeModal();
  // Optional explicit diagram (SupplierRoutePath.Diagram = '/supplier/diagram/:diagramId?') —
  // e.g. a draft's lineage.sourceDiagramId, or an upload result's diagram.id.
  const { diagramId: routeDiagramId } = useParams();
  const location = useLocation();

  // Resolve which diagram to show: an explicit route param first, else the
  // operator's most-recent installation diagram. Hooks run unconditionally;
  // the fetches are id-gated internally.
  const firstInstallationId = data.installations[0]?.id;
  const { data: installationDiagrams } = useFetchInstallationDiagrams(firstInstallationId);

  const resolvedDiagramId = useMemo(() => {
    if (routeDiagramId) return routeDiagramId;
    const list = installationDiagrams || [];
    if (list.length === 0) return null;
    // Most-recent first.
    const sorted = [...list].sort(
      (a, b) => new Date(b.createdAt || 0).getTime() - new Date(a.createdAt || 0).getTime(),
    );
    return sorted[0]?._id || null;
  }, [routeDiagramId, installationDiagrams]);

  // Back-link: to the originating draft when we arrived from its review page
  // (via router location state), otherwise the general Template Upload page.
  const cameFromDraft = location.state?.from === 'draft' && location.state?.draftId;
  const backTo = cameFromDraft
    ? buildSupplierDraftReviewPath(location.state.draftId)
    : SupplierRoutePath.Upload;
  const backLabel = cameFromDraft ? 'Back to draft' : 'Template Upload';

  // Fetches through the tenant-scoped supplier endpoint (item 1) — serves
  // both the operator's operational diagrams AND immutable source-evidence
  // diagrams, which the legacy GET /diagrams/:id 404s on. `isPending` alone
  // would read `true` even with no diagramId (react-query keeps a disabled
  // query's status at "pending"), so `loading` is gated on having one.
  const diagramQuery = useSupplierDiagram(resolvedDiagramId);
  const diagram = diagramQuery.data;
  const loading = !!resolvedDiagramId && diagramQuery.isPending;
  // R3 (S7 fix batch 3): a resolved diagramId whose fetch failed (transient
  // error, deleted diagram, bad deep link) previously matched none of the
  // empty/loading/diagram branches below — a blank 520px panel with no
  // explanation or way out.
  const hasError = !!resolvedDiagramId && !loading && diagramQuery.isError;

  const hasDiagram = !!resolvedDiagramId && !!diagram;

  return (
    <div className="flex flex-col gap-4">
      <div className="flex items-start justify-between gap-4">
        <div>
          <Link
            to={backTo}
            className="text-xs text-slate-400 no-underline hover:text-slate-600 inline-flex items-center gap-1.5"
          >
            <FontAwesomeIcon icon={faArrowLeft} />
            {backLabel}
          </Link>
          <h1 className="text-xl font-bold text-slate-800 m-0 mt-1">Production Diagram</h1>
          <p className="text-sm text-slate-500 mt-1 m-0">
            Built automatically from your communication template — share it as-is, view-only.
          </p>
        </div>
        {data.share.enabled ? (
          <Link
            to="/supplier/share"
            className="inline-flex items-center gap-1.5 text-xs font-semibold text-primaryColor border border-primaryColor/30 rounded-xl px-3 py-1.5 no-underline hover:bg-primaryColor/5 transition-colors"
          >
            <FontAwesomeIcon icon={faLink} />
            Shared on your portal
          </Link>
        ) : (
          <Link
            to="/supplier/share"
            className="inline-flex items-center gap-1.5 text-xs font-semibold text-white bg-primaryColor rounded-xl px-3 py-1.5 no-underline hover:bg-primaryColor/90 transition-colors"
          >
            <FontAwesomeIcon icon={faLink} />
            Share this diagram
          </Link>
        )}
      </div>

      <div
        className="relative rounded-2xl border border-slate-200 bg-white overflow-hidden"
        style={{ height: 520 }}
      >
        {/* Empty state — no diagram built yet */}
        {!resolvedDiagramId && !loading && (
          <div className="absolute inset-0 flex flex-col items-center justify-center text-center px-6">
            <div className="w-12 h-12 rounded-2xl bg-primaryColor/10 flex items-center justify-center mb-3">
              <FontAwesomeIcon
                icon={faCloudArrowUp}
                className="text-primaryColor"
              />
            </div>
            <p className="text-sm font-semibold text-slate-700 m-0">No production diagram yet</p>
            <p className="text-xs text-slate-400 mt-1 mb-3 max-w-sm">
              Upload your CBAM communication template and we'll build a shareable production diagram
              from it automatically.
            </p>
            <Link
              to={SupplierRoutePath.Upload}
              className="inline-flex items-center gap-1.5 text-xs font-semibold text-white bg-primaryColor rounded-xl px-3 py-2 no-underline hover:bg-primaryColor/90 transition-colors"
            >
              <FontAwesomeIcon icon={faCloudArrowUp} />
              Upload a template
            </Link>
          </div>
        )}

        {/* Loading — the panel already supplies the card chrome and a fixed
            520px height, so the loader renders bare and fills it. */}
        {resolvedDiagramId && loading && (
          <div className="absolute inset-0 flex items-center justify-center">
            <SupplierPanelLoader
              bare
              ariaLabel="Loading your production diagram"
              messages={[
                'Loading your production diagram',
                'Placing processes and precursors',
                'Wiring up the emission flows',
              ]}
              minHeight={0}
            />
          </div>
        )}

        {/* Error — R3: fetch failed (transient error, deleted diagram, bad
            deep link). Offer a retry and a way back instead of a blank panel. */}
        {hasError && (
          <div className="absolute inset-0 flex flex-col items-center justify-center text-center px-6">
            <div className="w-12 h-12 rounded-2xl bg-red-50 flex items-center justify-center mb-3">
              <FontAwesomeIcon
                icon={faTriangleExclamation}
                className="text-red-400"
              />
            </div>
            <p className="text-sm font-semibold text-slate-700 m-0">
              We couldn’t load this diagram
            </p>
            <p className="text-xs text-slate-400 mt-1 mb-4 max-w-sm">
              It may have been removed, or something went wrong loading it. Try again, or head back.
            </p>
            <div className="flex items-center gap-3">
              <CgRadixButton
                variant="outline"
                size="sm"
                icon={faRotateLeft}
                onClick={() => diagramQuery.refetch()}
              >
                Retry
              </CgRadixButton>
              <Link
                to={backTo}
                className="inline-flex items-center gap-1.5 text-xs font-semibold text-slate-500 no-underline hover:text-slate-700"
              >
                <FontAwesomeIcon icon={faArrowLeft} />
                {backLabel}
              </Link>
            </div>
          </div>
        )}

        {/* Real diagram — the shared read-only canvas, same as the public share page */}
        {hasDiagram && (
          <ReadOnlyDiagramCanvas
            nodes={diagram.nodes}
            edges={diagram.edges}
            reportingPeriod={diagram.reportingPeriod}
            diagramId={diagram?._id}
          >
            {(diagram.installationName || diagram.reportingPeriod) && (
              <div className="absolute top-3 left-1/2 -translate-x-1/2 z-10">
                {/* installationId deliberately omitted: the footer's lazy
                    country-flag lookup calls declarant-scoped
                    GET /installations/:id, which 403s for operators. */}
                <DiagramFooterPanel
                  installationName={diagram.installationName}
                  reportingPeriod={diagram.reportingPeriod}
                  reportingPeriodInteractive={false}
                />
              </div>
            )}

            {/* Locked SEFA / cost upsell — free tier renders the diagram but
                can't calculate emissions/costs on its nodes. */}
            <div className="absolute bottom-3 right-3 z-10">
              <button
                type="button"
                onClick={() => openUpgrade('sefa')}
                className="bg-white/95 backdrop-blur-sm border border-primaryColor/25 rounded-xl shadow-sm px-4 py-3 flex items-center gap-3 text-left hover:bg-primaryColor/5 transition-colors"
              >
                <div className="w-8 h-8 rounded-lg bg-primaryColor/15 flex items-center justify-center shrink-0">
                  <FontAwesomeIcon
                    icon={faLock}
                    className="text-primaryColor text-xs"
                  />
                </div>
                <div>
                  <p className="text-xs font-semibold text-slate-800 m-0">
                    Calculate SEFA &amp; CBAM cost for this diagram
                  </p>
                  <p className="text-[11px] text-slate-400 m-0">
                    Premium fills in emissions &amp; costs on every node
                  </p>
                </div>
              </button>
            </div>

            {/* View-only badge */}
            <div className="absolute top-3 right-3 z-10 inline-flex items-center gap-1.5 text-[10px] font-bold uppercase tracking-wider text-slate-400 bg-white/95 backdrop-blur-sm border border-slate-200 rounded-lg px-2.5 py-1.5">
              <FontAwesomeIcon icon={faEye} />
              View only
            </div>
          </ReadOnlyDiagramCanvas>
        )}
      </div>
    </div>
  );
}
